import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CoachingOverviewSafetyScoreSet, ShortCoachSession } from '../../../../backendsdk';
import { TrackedDialog as Dialog, TrackedIconButton as IconButton } from '../../../../components/TrackedComponents';
import CoachingEffectivenessRow from './CoachingEffectivenessRow';
import { HeaderTypography, StyledTableCell } from './common';

interface CoachingEffectivenessModalProps {
    open: boolean;
    onClose: () => void;
    effectivenessData: CoachingOverviewSafetyScoreSet[];
    autoSessions: ShortCoachSession[];
    tabsMap: Record<string, number>;
    setTab: (tabIndex: number) => void;
}

const TABLE_COLUMNS = [
    { key: 'driver', translationKey: 'content.coach.overview.effectiveness.driver' },
    { key: 'on_completion', translationKey: 'content.coach.overview.effectiveness.on_completion' },
    { key: 'after_two_weeks', translationKey: 'content.coach.overview.effectiveness.after_two_weeks' },
    { key: 'after_one_month', translationKey: 'content.coach.overview.effectiveness.after_one_month' },
    { key: 'after_three_months', translationKey: 'content.coach.overview.effectiveness.after_three_months' },
    { key: 'view_session', translationKey: 'content.coach.overview.effectiveness.view_session' },
    { key: 'send_new_session', translationKey: 'content.coach.overview.effectiveness.send_new_session' },
] as const;

const CoachingEffectivenessModal: React.FC<CoachingEffectivenessModalProps> = (
    props: CoachingEffectivenessModalProps,
) => {
    const { t } = useTranslation();
    const { open, onClose, effectivenessData, autoSessions, tabsMap, setTab } = props;
    const [searchText, setSearchText] = useState<string>('');

    // Reset search when modal closes
    useEffect(() => {
        if (!open) {
            setSearchText('');
        }
    }, [open]);

    const filteredData = useMemo(() => {
        // remove duplicate drivers
        let filtered = effectivenessData.filter(
            (row, index, self) => index === self.findIndex((t) => t.driver_name === row.driver_name),
        );

        // apply search filter
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase().trim();
            filtered = filtered.filter((row) => {
                const driverName = row.driver_name?.toLowerCase() || '';
                return driverName.includes(searchLower);
            });
        }

        return filtered;
    }, [effectivenessData, searchText]);

    return (
        <Dialog
            id="effectiveness-dialog"
            data-testid="effectiveness-dialog"
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                    paddingTop: '5vh',
                    '& .MuiPaper-root': {
                        width: '100%',
                        maxWidth: '1000px',
                        margin: 0,
                    },
                },
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'secondary.main',
                    position: 'sticky',
                    top: 0,
                    p: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5, px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '24px' }}>
                        {t('content.coach.overview.effectiveness.header')}
                    </Box>
                    <Box>
                        <TextField
                            id="effectiveness-search-field"
                            variant="standard"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            size="small"
                            placeholder={t('table.search')}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon color="primary" fontSize="small" sx={{ mb: 0.5, mr: 0.25 }} />
                                ),
                            }}
                            sx={{ minWidth: '200px' }}
                        />
                        <IconButton id="btn-close-modal" size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <TableContainer
                sx={{
                    maxHeight: 'calc(90vh - 120px)',
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader>
                    <colgroup>
                        <col />
                        <col width="12%" />
                        <col width="12%" />
                        <col width="12%" />
                        <col width="12%" />
                        <col width="12%" />
                        <col width="12%" />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            {TABLE_COLUMNS.map((column, index) => (
                                <StyledTableCell key={column.key} align={index === 0 ? undefined : 'center'}>
                                    <HeaderTypography variant="overline">{t(column.translationKey)}</HeaderTypography>
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={TABLE_COLUMNS.length} align="center" sx={{ py: 4 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Typography variant="overline">
                                            {t('content.coach.overview.effectiveness.no_data')}
                                        </Typography>
                                        {searchText.trim() && (
                                            <Link
                                                id="clear-search-link"
                                                component="button"
                                                variant="overline"
                                                onClick={() => setSearchText('')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {t('content.coach.overview.effectiveness.clear_search')}
                                            </Link>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredData.map((row, idx) => (
                                <CoachingEffectivenessRow
                                    key={row.session_id ?? idx}
                                    row={row}
                                    autoSessions={autoSessions}
                                    tabsMap={tabsMap}
                                    setTab={setTab}
                                    onClose={onClose}
                                    idx={idx}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Dialog>
    );
};

export default CoachingEffectivenessModal;
